import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

class WebSearchService {
  constructor() {
    this.booksCollection = 'pb-books';
  }

  // ============ SETTINGS ============
  
  async getWebSearchSettings(bookId) {
    try {
      const bookRef = doc(db, this.booksCollection, bookId);
      const bookDoc = await getDoc(bookRef);
      
      if (bookDoc.exists()) {
        return bookDoc.data().websearchSettings || null;
      }
      return null;
    } catch (error) {
      console.error('Error getting websearch settings:', error);
      throw error;
    }
  }

  async updateWebSearchSettings(bookId, settings) {
    try {
      const bookRef = doc(db, this.booksCollection, bookId);
      await updateDoc(bookRef, {
        websearchSettings: settings,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating websearch settings:', error);
      throw error;
    }
  }

  // ============ LEAD MANAGEMENT ============
  
  async saveItem(bookId, userId, itemData) {
    try {
      const itemsRef = collection(db, this.booksCollection, bookId, 'websearch-items');
      
      const { id, ...dataToSave } = itemData;
      
      const docRef = await addDoc(itemsRef, {
        ...dataToSave,
        userId: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { 
        id: docRef.id,
        userId: userId,
        ...dataToSave 
      };
    } catch (error) {
      console.error('Error saving item:', error);
      throw error;
    }
  }

  async getItems(bookId, filters = {}) {
    try {
      let itemsQuery = collection(db, this.booksCollection, bookId, 'websearch-items');
      const constraints = [];

      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters.saved !== undefined) {
        constraints.push(where('saved', '==', filters.saved));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      if (constraints.length > 0) {
        itemsQuery = query(itemsQuery, ...constraints);
      }

      const snapshot = await getDocs(itemsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting items:', error);
      throw error;
    }
  }

  async updateItem(bookId, itemId, updates) {
    try {
      const itemRef = doc(db, this.booksCollection, bookId, 'websearch-items', itemId);
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  }

  async deleteItem(bookId, itemId) {
    try {
      const itemRef = doc(db, this.booksCollection, bookId, 'websearch-items', itemId);
      await deleteDoc(itemRef);
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  }

  async bulkSaveItems(bookId, items, userId) {
    try {
      const batch = writeBatch(db);
      const itemsRef = collection(db, this.booksCollection, bookId, 'websearch-items');
      const savedItems = [];

      items.forEach(item => {
        const { id, ...dataToSave } = item;
        
        const newDocRef = doc(itemsRef);
        batch.set(newDocRef, {
          ...dataToSave,
          userId: userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        savedItems.push({
          id: newDocRef.id,
          userId: userId,
          ...dataToSave
        });
      });

      await batch.commit();
      return savedItems;
    } catch (error) {
      console.error('Error bulk saving items:', error);
      throw error;
    }
  }

  // ============ NOTES & ENGAGEMENT ============
  
  async addNote(bookId, itemId, noteData, userId) {
    try {
      const notesRef = collection(db, this.booksCollection, bookId, 'websearch-items', itemId, 'notes');
      const docRef = await addDoc(notesRef, {
        ...noteData,
        userId: userId,
        createdAt: serverTimestamp()
      });
      
      return { 
        id: docRef.id,
        userId: userId,
        ...noteData 
      };
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  }

  async getNotes(bookId, itemId) {
    try {
      const notesRef = collection(db, this.booksCollection, bookId, 'websearch-items', itemId, 'notes');
      const notesQuery = query(notesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(notesQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting notes:', error);
      throw error;
    }
  }

  async updateNote(bookId, itemId, noteId, updates) {
    try {
      const noteRef = doc(db, this.booksCollection, bookId, 'websearch-items', itemId, 'notes', noteId);
      await updateDoc(noteRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  }

  async deleteNote(bookId, itemId, noteId) {
    try {
      const noteRef = doc(db, this.booksCollection, bookId, 'websearch-items', itemId, 'notes', noteId);
      await deleteDoc(noteRef);
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  }

  // ============ GROUNDING URLS ============
  
  async saveGroundingUrls(bookId, urls) {
    try {
      const bookRef = doc(db, this.booksCollection, bookId);
      await updateDoc(bookRef, {
        'websearchSettings.groundingUrls': urls,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving grounding URLs:', error);
      throw error;
    }
  }

  async createSearchPrompt(bookId, promptData) {
    try {
      const promptsRef = collection(db, this.booksCollection, bookId, 'search-prompts');
      const docRef = await addDoc(promptsRef, {
        ...promptData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...promptData };
    } catch (error) {
      console.error('Error creating search prompt:', error);
      throw error;
    }
  }

  async getSearchPrompts(bookId) {
    try {
      const promptsRef = collection(db, this.booksCollection, bookId, 'search-prompts');
      const promptsQuery = query(promptsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(promptsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting search prompts:', error);
      throw error;
    }
  }

  async updateSearchPrompt(bookId, promptId, updates) {
    try {
      const promptRef = doc(db, this.booksCollection, bookId, 'search-prompts', promptId);
      await updateDoc(promptRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating search prompt:', error);
      throw error;
    }
  }

  async deleteSearchPrompt(bookId, promptId) {
    try {
      const promptRef = doc(db, this.booksCollection, bookId, 'search-prompts', promptId);
      await deleteDoc(promptRef);
    } catch (error) {
      console.error('Error deleting search prompt:', error);
      throw error;
    }
  }

  async createLeadMessage(bookId, messageData) {
    try {
      const messagesRef = collection(db, this.booksCollection, bookId, 'lead-messages');
      const docRef = await addDoc(messagesRef, {
        ...messageData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...messageData };
    } catch (error) {
      console.error('Error creating lead message:', error);
      throw error;
    }
  }

  async getLeadMessages(bookId) {
    try {
      const messagesRef = collection(db, this.booksCollection, bookId, 'lead-messages');
      const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(messagesQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting lead messages:', error);
      throw error;
    }
  }

  async updateLeadMessage(bookId, messageId, updates) {
    try {
      const messageRef = doc(db, this.booksCollection, bookId, 'lead-messages', messageId);
      await updateDoc(messageRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating lead message:', error);
      throw error;
    }
  }

  async deleteLeadMessage(bookId, messageId) {
    try {
      const messageRef = doc(db, this.booksCollection, bookId, 'lead-messages', messageId);
      await deleteDoc(messageRef);
    } catch (error) {
      console.error('Error deleting lead message:', error);
      throw error;
    }
  }
}

export const websearchService = new WebSearchService();